import {
  getAdjacentCaseSidebarUrlSection,
  getCaseSidebarNavigationLabel,
} from '@components/CaseSidebar/navigation/caseSidebarFormNavigation';
import { isNestedFormSection, resolveFormSection } from '@components/CaseSidebar/navigation/formSectionMapping';
import type { ComponentType, LazyExoticComponent } from 'react';
import { lazy } from 'react';
import { useIntl } from 'react-intl';
import { useSearchParams } from 'react-router-dom';
import { ApplicationStatus, BtlApplication } from '../../api/btlApplicationForm/types';
import { Financing } from './FinancingAndProperty/Financing';
import { Property } from './FinancingAndProperty/Property';
import { BtlApplicationFormPageEnum, BtlFormRoute } from './types';

type FormPageComponent = LazyExoticComponent<ComponentType<any>> | ComponentType<any>;

const STANDALONE_FOOTER_SECTIONS = new Set<BtlApplicationFormPageEnum>([
  BtlApplicationFormPageEnum.APPLICANTS,
  BtlApplicationFormPageEnum.PRODUCT,
  BtlApplicationFormPageEnum.TOP_SLICING,
  BtlApplicationFormPageEnum.AFFORDABILITY_CHECK,
  BtlApplicationFormPageEnum.COMMUNICATION,
  BtlApplicationFormPageEnum.DOCUMENTS,
  BtlApplicationFormPageEnum.VALUATION,
  BtlApplicationFormPageEnum.POST_OFFER,
]);

const NESTED_FORM_ROUTE_PARENTS: Partial<Record<BtlApplicationFormPageEnum, BtlApplicationFormPageEnum>> = {
  [BtlApplicationFormPageEnum.APPLICANTS_INDIVIDUAL]: BtlApplicationFormPageEnum.APPLICANTS,
  [BtlApplicationFormPageEnum.APPLICANTS_COMPANY]: BtlApplicationFormPageEnum.APPLICANTS,
  [BtlApplicationFormPageEnum.TOP_SLICING_HOUSEHOLD]: BtlApplicationFormPageEnum.TOP_SLICING,
};

const btlFormPageRegistry: Partial<Record<BtlApplicationFormPageEnum, FormPageComponent>> = {
  [BtlApplicationFormPageEnum.CASE_SUMMARY]: lazy(() => import('./CaseSummary/CaseSummary')),
  [BtlApplicationFormPageEnum.FINANCING]: Financing,
  [BtlApplicationFormPageEnum.PROPERTY]: Property,
  [BtlApplicationFormPageEnum.APPLICANTS]: lazy(() => import('./Applicants/Applicants')),
  [BtlApplicationFormPageEnum.APPLICANTS_COMPANY]: lazy(() => import('./Applicants/Company/Company')),
  [BtlApplicationFormPageEnum.APPLICANTS_INDIVIDUAL]: lazy(() => import('./Applicants/Individual/Individual')),
  [BtlApplicationFormPageEnum.PRODUCT]: lazy(() => import('./Product/ProductSelector')),
  [BtlApplicationFormPageEnum.TOP_SLICING]: lazy(() => import('./TopSlicing/TopSlicing')),
  [BtlApplicationFormPageEnum.TOP_SLICING_HOUSEHOLD]: lazy(() => import('./TopSlicing/Household/Household')),
  [BtlApplicationFormPageEnum.AFFORDABILITY_CHECK]: lazy(
    () => import('@pages/BtlApplicationFormPage/common/AffordabilityCheck'),
  ),
  [BtlApplicationFormPageEnum.SUBMIT_DIP]: lazy(() => import('./SubmitDip/SubmitDip')),
  [BtlApplicationFormPageEnum.SOLICITORS]: lazy(() => import('./Solicitors/Solicitors')),
  [BtlApplicationFormPageEnum.BANK_DETAILS]: lazy(() => import('./BankDetails/BankDetails')),
  [BtlApplicationFormPageEnum.COMMUNICATION]: lazy(() =>
    import('./Notes/BtlNotes').then(module => ({ default: module.BtlNotes })),
  ),
  [BtlApplicationFormPageEnum.DOCUMENTS]: lazy(() =>
    import('@pages/BtlApplicationFormPage/Documents').then(module => ({ default: module.Documents })),
  ),
  [BtlApplicationFormPageEnum.VALUATION]: lazy(() =>
    import('./Valuation/BtlValuation').then(module => ({ default: module.BtlValuation })),
  ),
  [BtlApplicationFormPageEnum.POST_OFFER]: lazy(() =>
    import('./PostOffer/BtlPostOffer').then(module => ({ default: module.BtlPostOffer })),
  ),
  [BtlApplicationFormPageEnum.ADDITIONAL_INFORMATION]: lazy(
    () => import('./AdditionalInformation/AdditionalInformation'),
  ),
};

function isFormEditable(section?: BtlApplicationFormPageEnum, data?: BtlApplication): boolean {
  if (section === BtlApplicationFormPageEnum.VALUATION || section === BtlApplicationFormPageEnum.POST_OFFER) {
    return false;
  }

  return data?.status === ApplicationStatus.NEW || data?.status === ApplicationStatus.DIP_ACCEPTED || !data;
}

export const useBtlFormMappedRoutes = (applicationData?: BtlApplication): BtlFormRoute => {
  const { formatMessage } = useIntl();
  const [searchParams] = useSearchParams();
  const formSectionParam = searchParams.get('section');
  const currentSection = resolveFormSection(formSectionParam) as BtlApplicationFormPageEnum | undefined;
  const FormPage = currentSection ? btlFormPageRegistry[currentSection] : undefined;
  const isNestedFormRoute = isNestedFormSection(formSectionParam);
  const nestedParentSection = currentSection ? NESTED_FORM_ROUTE_PARENTS[currentSection] : undefined;
  const navigationApplication =
    applicationData ?? ({ status: ApplicationStatus.NEW, isTopSlicing: false } as BtlApplication);

  const previousSection = isNestedFormRoute
    ? nestedParentSection
    : formSectionParam
    ? getAdjacentCaseSidebarUrlSection(formSectionParam, navigationApplication, 'previous')
    : undefined;

  const nextSection = isNestedFormRoute
    ? nestedParentSection
    : formSectionParam
    ? getAdjacentCaseSidebarUrlSection(formSectionParam, navigationApplication, 'next')
    : undefined;

  const previousLabel = isNestedFormRoute
    ? currentSection
      ? formatMessage({ id: `btlApplicationForm.${currentSection}.previousLabel` as any })
      : undefined
    : formSectionParam
    ? getCaseSidebarNavigationLabel(formatMessage, formSectionParam, navigationApplication, 'previous')
    : undefined;

  const nextLabel = isNestedFormRoute
    ? currentSection
      ? formatMessage({ id: `btlApplicationForm.${currentSection}.nextLabel` as any })
      : undefined
    : formSectionParam
    ? getCaseSidebarNavigationLabel(formatMessage, formSectionParam, navigationApplication, 'next')
    : undefined;

  return {
    FormPage: FormPage as BtlFormRoute['FormPage'],
    currentSection,
    nextSection,
    previousSection,
    nextLabel,
    previousLabel,
    isFormEditable: isFormEditable(currentSection, applicationData),
    showStandaloneFormFooter: currentSection ? STANDALONE_FOOTER_SECTIONS.has(currentSection) : false,
  };
};
